import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Sparkles, ShieldCheck, Infinity as InfinityIcon, FileDown, Bell, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const FEATURES = [
  { icon: InfinityIcon, text: "Unlimited AI Legal Companion messages" },
  { icon: FolderOpen, text: "Unlimited saved cases in your vault" },
  { icon: FileDown, text: "Export Prep Briefs & Diagnostic plans as PDF" },
  { icon: Bell, text: "Court-date reminders & deadline nudges" },
  { icon: ShieldCheck, text: "Everything in the free plan, unlocked" },
];

export default function Upgrade() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const buy = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "case_pass_90d",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="grain-bg min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <span className="font-mono text-xs uppercase tracking-[0.18em] text-terracotta">Case Pass</span>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-semibold text-forest-900">Unlock everything for your case</h1>
          <p className="mt-4 text-ink-700 max-w-xl mx-auto">One flat price. No subscription. Full access to every tool for 90 days — enough time to get through the hardest part of your case.</p>
        </div>

        {user?.is_pro ? (
          <div data-testid="pro-active-card" className="mt-10 rounded-3xl border border-forest-600/30 bg-forest-50 p-8 text-center">
            <span className="grid place-items-center h-14 w-14 rounded-2xl bg-forest-800 text-parchment-50 mx-auto"><ShieldCheck className="h-7 w-7" /></span>
            <h2 className="mt-4 font-serif text-2xl font-semibold text-forest-900">Your Case Pass is active</h2>
            {user.case_pass_expires_at && <p className="mt-1 text-ink-600">Access through {new Date(user.case_pass_expires_at).toLocaleDateString()}</p>}
            <Button onClick={() => navigate("/dashboard")} className="mt-6 rounded-full bg-forest-800 hover:bg-forest-900 text-parchment-50">Go to my vault</Button>
          </div>
        ) : (
          <div className="mt-10 rounded-3xl border border-parchment-200 bg-white overflow-hidden shadow-[0_20px_60px_rgba(27,59,50,0.1)]">
            <div className="bg-forest-900 text-parchment-50 p-8 text-center">
              <div className="flex items-end justify-center gap-1">
                <span className="font-serif text-6xl font-semibold">$20</span>
                <span className="text-parchment-200/70 mb-2">one-time</span>
              </div>
              <p className="mt-2 text-parchment-200/80">90 days of full access</p>
            </div>
            <div className="p-8">
              <ul className="space-y-3">
                {FEATURES.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-ink-700">
                    <span className="grid place-items-center h-8 w-8 rounded-lg bg-forest-100 text-forest-800 shrink-0"><f.icon className="h-4 w-4" /></span>
                    {f.text}
                  </li>
                ))}
              </ul>
              <Button onClick={buy} data-testid="buy-case-pass-btn" disabled={loading}
                className="mt-8 w-full rounded-xl h-12 bg-terracotta hover:bg-terracotta-hover text-white gap-2 text-base">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Sparkles className="h-5 w-5" /> Get the Case Pass</>}
              </Button>
              <p className="mt-3 text-center text-xs text-ink-400">Secure checkout by Stripe · Test card 4242 4242 4242 4242</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
